// =====================================================================
// src/lib/reservas.js — o que a tela de Reservas decide, sem DOM
//
// Puro. A regra (quem lança onde, overbooking, o valor) mora em 0015; aqui
// se classifica, agrupa, busca, valida o formulário e formata datas — tudo
// testável em node.
// =====================================================================
// `diaDe` e `horaDe` vêm da auditoria e não são reescritos aqui: os dois
// já convertem timestamp para o fuso do hotel com Intl, e já têm assert.
// Os dois arquivos são folhas (nenhum importa ninguém), então não há
// ciclo — conferido antes de escrever esta linha.
import { diaDe, horaDe } from './auditoria.js';

const DIA = 86400000;
export function dataISO(d) { const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; }
export function somarDias(iso, n) { const [a, m, d] = iso.split('-').map(Number); const x = new Date(a, m - 1, d + n); return dataISO(x); }
// 0096 (redundancias-2, decisão 3A do Hugo): UMA régua para "o quarto está
// ocupado nesta noite?". A reserva ocupa as noites [check_in, fim): fim é o
// check_out previsto — ou, se ela foi concluída antes, o dia do HOTEL em
// que o hóspede saiu (`checkout_dia`, gravado pelo banco no check-out). É a
// mesma conta do banco, que já liberava o quarto na saída antecipada; antes
// a tela seguia contando a concluída até o fim e via overbooking falso.
// Disponibilidade, Quadro, Dashboard e Trocas perguntam AQUI.
export function fimDaOcupacao(r) {
  if (!r || !r.check_in || !r.check_out) return null;
  if (r.situacao === 'concluida' && r.checkout_dia && r.checkout_dia < r.check_out) return r.checkout_dia > r.check_in ? r.checkout_dia : r.check_in;
  return r.check_out;
}
export function ocupaANoite(r, dia) {
  if (!r || r.situacao === 'cancelada' || r.situacao === 'noshow') return false;
  const fim = fimDaOcupacao(r);
  return !!fim && r.check_in <= dia && dia < fim;
}
export function noites(checkIn, checkOut) {
  const a = Date.parse(checkIn + 'T12:00:00'), b = Date.parse(checkOut + 'T12:00:00');
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / DIA));
}
// "13/09" ou "13/09/26" — curta, para a lista; com o ano só se não for o corrente.
export function dataCurta(iso, hoje) {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  const anoAtual = (hoje || dataISO(new Date())).slice(0, 4);
  return a === anoAtual ? `${d}/${m}` : `${d}/${m}/${a.slice(2)}`;
}
export function periodo(r, hoje) { return `${dataCurta(r.check_in, hoje)} → ${dataCurta(r.check_out, hoje)} · ${noites(r.check_in, r.check_out)} noite(s)`; }

// A situação operacional de uma reserva vista de hoje. Cancelada e no-show
// vêm como estão; confirmada se divide em passada, sai hoje, hospedada,
// chega hoje, futura — é o que a recepção quer saber de cada uma.
export const FASES = Object.freeze({
  chega: { rotulo: 'Chega hoje', classe: 'badge-blue' },
  sai: { rotulo: 'Sai hoje', classe: 'badge-yellow' },
  hospedada: { rotulo: 'Hospedada', classe: 'badge-green' },
  futura: { rotulo: 'Futura', classe: 'badge-gray' },
  passada: { rotulo: 'Encerrada', classe: 'badge-gray' },
  cancelada: { rotulo: 'Cancelada', classe: 'badge-red' },
  noshow: { rotulo: 'No-show', classe: 'badge-red' },
  concluida: { rotulo: 'Concluída', classe: 'badge-gray' },
});
export function fase(r, hoje) {
  if (r.situacao === 'cancelada') return 'cancelada';
  if (r.situacao === 'noshow') return 'noshow';
  if (r.situacao === 'concluida') return 'concluida';
  if (r.check_in === hoje) return 'chega';
  if (r.check_out === hoje) return 'sai';
  if (r.check_in < hoje && r.check_out > hoje) return 'hospedada';
  if (r.check_in > hoje) return 'futura';
  return 'passada';
}

// As abas da tela, com o que cada uma mostra. A ordem dentro de cada aba:
// por check-in, depois por quarto.
export const ABAS = Object.freeze([
  { chave: 'hoje', rotulo: 'Hoje', fases: ['chega', 'sai', 'hospedada'] },
  { chave: 'proximas', rotulo: 'Próximas', fases: ['futura'] },
  { chave: 'encerradas', rotulo: 'Encerradas', fases: ['passada', 'concluida', 'cancelada', 'noshow'] },
  // Pedido do Raimundo na reunião de 18/09: ver o que foi lançado no dia.
  //
  // Esta aba corta por outro EIXO que as três de cima. As outras perguntam
  // "onde esta reserva está na vida dela" (chega, sai, hospedada, futura);
  // esta pergunta "quando ela entrou no sistema" — e as duas não se
  // encaixam. Uma reserva lançada hoje para o Natal é `futura` E
  // `lancada`, e tem de aparecer nas duas.
  //
  // `fases: []` deixa isso explícito: quem filtra olha `porLancamento`
  // primeiro, e a lista de fases nunca é consultada aqui.
  { chave: 'lancadas', rotulo: 'Lançadas hoje', fases: [], porLancamento: true },
]);
const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const chaveQuarto = n => { const m = /^(\d+)(.*)$/.exec(String(n || '')); return m ? m[1].padStart(6, '0') + m[2].toLowerCase() : 'z' + String(n || '').toLowerCase(); };

export function hospedePrincipal(r) {
  const h = Array.isArray(r.hospedes) ? r.hospedes : [];
  const p = h.find(x => x.principal) || h[0];
  return p ? p.nome : null;
}

// A reserva foi lançada NESTE dia do hotel?
//
// O corte é o dia do HOTEL (0020), não o do aparelho nem o UTC: um hotel
// em São Paulo que vira à meia-noite tem reservas lançadas às 23h40 que
// são de hoje, e o `criado_em` delas em UTC já está no dia seguinte.
// Contar pelo UTC jogaria o fim do expediente para o relatório de amanhã
// — justamente as últimas do dia, que é o que se confere à noite.
//
// `diaDe` vem de `lib/auditoria.js`, que já resolveu isto com Intl e já é
// provado. Escrever uma segunda conversão aqui seria uma segunda versão
// da mesma verdade — a primeira a divergir.
export function lancadaNoDia(r, dia, fuso) {
  return !!(r && r.criado_em) && diaDe(r.criado_em, fuso) === dia;
}

// "19/09 14:32" — o formato do `_fmtDataHoraCurta` do Monteiro de Moraes
// (index.html, 16658), que é como ele mostra a marca de lançamento. Lá
// ele usa a hora do APARELHO; aqui usa a do hotel, porque o Garvey e quem
// olha a tela podem não estar no mesmo fuso, e a hora tem de ser a mesma
// que a trilha do apartamento mostra para o mesmo evento.
//
// No dia corrente o "19/09" some e fica só a hora: quem está na aba de
// hoje já sabe que dia é, e repetir a data em toda linha é ruído.
export function lancadaEm(r, fuso, hoje = null) {
  if (!r || !r.criado_em) return '';
  const dia = diaDe(r.criado_em, fuso);
  const hora = horaDe(r.criado_em, fuso);
  if (!dia || !hora) return '';
  if (hoje && dia === hoje) return hora;
  const [, m, d] = dia.split('-');
  return `${d}/${m} ${hora}`;
}

// Quantas entraram no dia, e quantas delas já foram canceladas depois.
// A segunda importa: uma reserva lançada e cancelada no mesmo dia conta
// como trabalho feito e desfeito, e some de qualquer contagem que olhe só
// as confirmadas.
export function resumoDasLancadas(lista, dia, fuso) {
  const l = (lista || []).filter(r => lancadaNoDia(r, dia, fuso));
  return {
    total: l.length,
    canceladas: l.filter(r => r.situacao === 'cancelada' || r.situacao === 'noshow').length,
  };
}

export function filtrarReservas(lista, { aba = 'hoje', texto = '' } = {}, hoje, fuso = 'UTC') {
  const def = ABAS.find(a => a.chave === aba) || ABAS[0];
  const t = semAcento(texto).trim();
  return (lista || [])
    .map(r => ({ ...r, fase: fase(r, hoje), quarto: r.unidades ? r.unidades.numero : (r.quarto || ''), hospede: hospedePrincipal(r) }))
    .filter(r => t
      ? [r.quarto, r.numero, r.numero_ota, r.plataforma, r.hospede, r.imobiliarias && r.imobiliarias.nome].some(c => semAcento(c).includes(t))
      : (def.porLancamento ? lancadaNoDia(r, hoje, fuso) : def.fases.includes(r.fase)))
    .sort((a, b) => {
      // Na aba das lançadas a ordem é a do RELÓGIO, da mais recente para a
      // mais antiga: quem confere o dia quer ver primeiro o que acabou de
      // entrar. Ordenar por check-in ali misturaria uma reserva de
      // dezembro lançada agora com uma de amanhã lançada de manhã, e a
      // pergunta "o que foi lançado hoje" some.
      if (def.porLancamento) return String(b.criado_em || '').localeCompare(String(a.criado_em || ''));
      const ordemFase = ['chega', 'sai', 'hospedada', 'futura', 'passada', 'concluida', 'cancelada', 'noshow'];
      return (ordemFase.indexOf(a.fase) - ordemFase.indexOf(b.fase)) || a.check_in.localeCompare(b.check_in) || chaveQuarto(a.quarto).localeCompare(chaveQuarto(b.quarto));
    });
}

// Contadores do cabeçalho de "Hoje".
export function resumoDoDia(lista, hoje) {
  const f = (lista || []).map(r => fase(r, hoje));
  return { chegam: f.filter(x => x === 'chega').length, saem: f.filter(x => x === 'sai').length, hospedadas: f.filter(x => x === 'hospedada').length };
}

// Os quartos que se oferecem no formulário, por andar; cada um com a
// imobiliária (a recepção precisa saber de quem é o quarto).
export function quartosParaReserva(unidades) {
  const por = new Map();
  for (const u of (unidades || []).filter(u => u.ativo !== false)) {
    const k = u.andar === null || u.andar === undefined ? null : Number(u.andar);
    if (!por.has(k)) por.set(k, []);
    por.get(k).push(u);
  }
  return [...por.entries()].sort((a, b) => (a[0] === null ? 1 : b[0] === null ? -1 : a[0] - b[0]))
    .map(([andar, qs]) => ({ andar, rotulo: andar === null ? 'Sem andar' : `${andar}º andar`, quartos: qs.slice().sort((x, y) => chaveQuarto(x.numero).localeCompare(chaveQuarto(y.numero))) }));
}

export const PLATAFORMAS = Object.freeze(['Direto', 'Booking', 'Airbnb', 'Expedia', 'Decolar', 'Hoteis.com', 'Outra']);

// dd/mm/aaaa — a data por extenso da tabela do MdM (`fmtDate`). A `dataCurta`
// continua servindo onde o espaço é curto (o cartão do celular).
export function dataBR(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).split('-');
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

// A cor da pílula de plataforma, copiada de `platBadgeMini` (MdM, 6872):
// Airbnb vermelho, Booking azul, Direto verde, o resto cinza. O MdM só
// conhece quatro; aqui as outras OTAs (Expedia, Decolar, Hoteis.com) caem no
// cinza dele em vez de ganharem cor nova — inventar cor é o que a regra do
// CLAUDE.md proíbe.
const CORES_PLATAFORMA = Object.freeze({ airbnb: '#dc2626', booking: '#2563eb', direto: '#16a34a' });
export function corDaPlataforma(p) {
  return CORES_PLATAFORMA[String(p || '').trim().toLowerCase()] || '#6b7280';
}

export function validarReserva({ unidade_id, check_in, check_out }) {
  const erros = [];
  if (!unidade_id) erros.push('Escolha o quarto');
  if (!check_in || !check_out) erros.push('Informe check-in e check-out');
  else if (check_out <= check_in) erros.push('O check-out tem de ser depois do check-in');
  else if (noites(check_in, check_out) > 366) erros.push('Reserva com mais de um ano — confira as datas');
  return erros;
}

// O que se pode fazer com uma reserva, para a tela desenhar os botões.
// Alguém da reserva fez check-in e ainda não saiu? (0084: com hóspede dentro
// não se cancela nem se marca no-show — o banco recusa; a tela nem oferece.)
// Sem a lista de hóspedes (perfil sem o módulo Hóspedes), responde false e
// quem decide é o banco.
export const comHospedeDentro = r => (r.hospedes || []).some(h => h && h.chegada_em && !h.saida_em);

export function acoesDaReserva(r, { podeEditar, temFinanceiro, minhaImobiliaria }, hoje) {
  if (!podeEditar && !temFinanceiro) return [];
  const a = [];
  const f = fase(r, hoje);
  const confirmada = r.situacao === 'confirmada';
  const dentro = comHospedeDentro(r);
  if (podeEditar && confirmada) { a.push('editar'); if (!dentro) a.push('cancelar'); }
  if (podeEditar && confirmada && r.check_in <= hoje && !dentro) a.push('noshow');
  // o valor: só quem tem financeiro, só nas da própria imobiliária
  if (temFinanceiro && (minhaImobiliaria === null || r.imobiliaria_id === minhaImobiliaria)) a.push('valor');
  void f;
  return a;
}

export function moeda(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v); if (Number.isNaN(n)) return '—';
  return 'R$ ' + n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

// ── AS TELAS DO MÓDULO (a barra do MdM, `_reservasTabBar`, 2508) ──────
// Lá são seis: Lista · Quadro · Disponibilidade · Confirmação · Trocas
// Futuras · Lançamento Sistema. Entram aqui na ordem dele, conforme vão
// sendo copiadas — uma aba que leva a "em construção" é pior que aba
// nenhuma. `icone` é o símbolo do sprite; `emoji` é o que lá é emoji.
export const TELAS = Object.freeze([
  { chave: 'lista',           rotulo: 'Lista',           icone: 'ic-tarefas' },
  { chave: 'quadro',          rotulo: 'Quadro',          icone: 'ic-quadro' },
  { chave: 'disponibilidade', rotulo: 'Disponibilidade', emoji: '🔍' },
  { chave: 'trocas',          rotulo: 'Trocas Futuras',  emoji: '🔄' },
]);
// 0068 (Hugo, 27/09): Trocas Futuras é da escala — o hotel a vê em Escalas
// e folgas. Em Reservas ela fica só para a imobiliária (visão 'dono').
export function telasDe(visao) {
  return TELAS.filter(t => t.chave !== 'trocas' || visao === 'dono');
}

// ── QUEM VÊ O QUÊ ────────────────────────────────────────────────────
// Como em `visitas.js` e `garagem.js`: a imobiliária vem antes do nível.
// A imobiliária (Monteiro de Moraes) é 'dono' — a RLS (0003) já corta as
// reservas dela às dela. (O login de proprietário, que também caía aqui,
// saiu na 0101: o dono não tem acesso ao sistema.)
//
//   dono      as reservas DELA: Lista (só leitura ou não, conforme o
//             nível) e Trocas Futuras dos SEUS quartos — "para ele
//             controlar seu check in e out na semana" (Hugo, 20/09)
//   balcao    recepção e administração (`editar`): o prédio inteiro
//   leitura   quem só vê (`ver`): o prédio inteiro, sem botão
//   null      sem o módulo
export function visaoDe(contexto) {
  const c = contexto || {};
  const m = (Array.isArray(c.modulos) ? c.modulos : []).find(x => x && x.chave === 'reservas');
  if (!m) return null;
  if (c.imobiliaria && c.imobiliaria.id) return 'dono';
  return m.nivel === 'editar' ? 'balcao' : 'leitura';
}

// A legenda debaixo do título (Hugo "4 A"). Nulo = a regra padrão.
export function legendaDe(contexto) {
  return visaoDe(contexto) === 'dono' ? 'Reservas dos seus apartamentos' : null;
}
